import React, { useEffect, useState } from "react";
import { Plus, Trash2, Check, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { TRY, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const UNITS = ["Adet", "Kg", "Lt", "Paket", "Koli"];
const empty = { name: "", unit: "Adet", price: "" };

export default function SupplierProducts({ open, onClose, suppliers, defaultId, onChanged }) {
  const [sid, setSid] = useState("");
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  useEffect(() => { if (open) { setSid(defaultId || suppliers[0]?.id || ""); setForm(empty); setEditId(null); } /* eslint-disable-next-line */ }, [open]);
  const load = async (s) => { if (!s) return setProducts([]); const r = await api.get("/supplier-products", { params: { supplier_id: s } }); setProducts(r.data); };
  useEffect(() => { load(sid); }, [sid]);

  const save = async () => {
    if (!form.name.trim() || !(parseFloat(form.price) >= 0)) { toast.error("Ürün adı ve fiyat gerekli"); return; }
    const body = { supplier_id: sid, name: form.name.trim(), unit: form.unit, price: parseFloat(form.price) || 0 };
    try {
      if (editId) await api.put(`/supplier-products/${editId}`, body); else await api.post("/supplier-products", body);
      toast.success(editId ? "Ürün güncellendi" : "Ürün eklendi"); setForm(empty); setEditId(null); load(sid); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/supplier-products/${id}`); toast.success("Ürün silindi"); load(sid); onChanged?.(); };
  const edit = (p) => { setEditId(p.id); setForm({ name: p.name, unit: p.unit, price: String(p.price) }); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="supplier-products-dialog">
        <DialogHeader><DialogTitle className="font-heading">Tedarikçi Ürünleri & Fiyatları</DialogTitle></DialogHeader>
        <Select value={sid} onValueChange={setSid}>
          <SelectTrigger data-testid="products-supplier-select"><SelectValue placeholder="Tedarikçi seçin" /></SelectTrigger>
          <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <div className="rounded-xl border border-border divide-y divide-border/60 max-h-64 overflow-y-auto" data-testid="products-list">
          {products.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground" data-testid="products-empty">Bu tedarikçi için ürün tanımlı değil</div>}
          {products.map((p) => (
            <div key={p.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`product-row-${p.id}`}>
              <div className="flex-1 min-w-0"><div className="font-medium truncate">{p.name}</div><div className="text-xs text-muted-foreground">{p.unit}</div></div>
              <div className="font-mono font-semibold">{TRY(p.price)}</div>
              <button onClick={() => edit(p)} data-testid={`product-edit-${p.id}`} className="h-7 w-7 rounded-md hover:bg-accent inline-flex items-center justify-center text-muted-foreground"><Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={() => del(p.id)} data-testid={`product-delete-${p.id}`} className="h-7 w-7 rounded-md hover:bg-destructive/10 inline-flex items-center justify-center text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-end">
          <Input placeholder="Ürün adı (örn. Normal Ekmek)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" />
          <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
            <SelectTrigger className="w-24" data-testid="product-unit-select"><SelectValue /></SelectTrigger>
            <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" step="0.01" placeholder="Fiyat ₺" className="w-24 font-mono" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price-input" />
          <Button onClick={save} disabled={!sid} data-testid="product-save-btn" className="gap-1">{editId ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}{editId ? "Güncelle" : "Ekle"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
